import {
  AdyenCheckout,
  ApplePay,
  Bancontact,
  Blik,
  Card,
  GooglePay,
  MBWay,
  Multibanco,
  Redirect,
  Twint,
  BcmcMobile,
  ICore,
  CheckoutAdvancedFlowResponse,
  Swish,
} from 'adyen-web-new';

import { IAdyenWebPaymentResponse, ICheckoutRedirectAction } from '@model';
import { ELogLevel, LoggerType } from '@services/errorService';

import {
  APPLE_PAY_SERVICE,
  BANCONTACT_MOBILE_SERVICE,
  BANCONTACT_SERVICE,
  BLIK_SERVICE,
  CARD_SERVICE,
  GOOGLE_PAY_SERVICE,
  IDEAL_SERVICE,
  MBWAY_SERVICE,
  MOBILE_PAY_SERVICE,
  MULTIBANCO_SERVICE,
  SWISH_SERVICE,
  THREE_DS_ONE_PARAM_MD,
  THREE_DS_ONE_PARAM_PA_REQ,
  THREE_DS_ONE_PARAM_TERM_URL,
  TWINT_SERVICE,
} from '../constants';
import { mapAdyenActionFromDomain, mapAdyenConfigurationFromDomain, mapAdyenEnvironmentFromDomain } from '../mapper';
import {
  IInitializeAdyenApi,
  TAdyenAction,
  TAdyenService,
  TAdyenServiceSettings,
  TAdyenActionElement,
  IAdyenPlaceholderMessages,
  IOnSubmitActionProps,
} from '../model';

import { createAndSubmitThreeDsAuthForm } from './threeDsOneSubmitForm';

/**
 * This method initializes adyen library with proper configuration.
 * @remarks This method does not mount library into DOM. It returns library which should be used to mount components into element.
 *
 * @param configuration - basic adyen library configuration
 * @param availablePaymentMethods - available methods and stored payment methods for user
 * @param onChange - on change event that will be triggered when user fills in payment form
 * @param onAdditionalDetail - event that will be called after library performs additional action (e.g. 3DS2 challange)
 * @param messages - localization messages for adyen component
 */
export const initializeAdyen = async ({
  configuration,
  availablePaymentMethods,
  onChange,
  onSubmit,
  onAdditionalDetail,
  messages,
}: IInitializeAdyenApi): Promise<ICore> => {
  return await AdyenCheckout({
    ...configuration,
    paymentMethodsResponse: availablePaymentMethods && mapAdyenConfigurationFromDomain(availablePaymentMethods),
    onAdditionalDetails: onAdditionalDetail,
    onChange: onChange,
    onSubmit: onSubmit,
    environment: mapAdyenEnvironmentFromDomain(configuration.environment),
    countryCode: configuration.locale,
    translations: messages && {
      [configuration.locale]: {
        'creditCard.numberField.title': messages.cardNumber,
        'creditCard.expiryDateField.title': messages.expiration,
        'creditCard.cvcField.title': messages.cvvNumber,
        'creditCard.holderName': messages.cardHolder,
        'error.va.sf-cc-num.01': messages.invalidCardNumber,
        'creditCard.numberField.invalid': messages.incorrectCreditCardFormat,
        'creditCard.expiryDateField.invalid': messages.expiration,
        'creditCard.cvcField.invalid': messages.cvvNumber,
        'creditCard.holderName.invalid': messages.cardHolder,
        'creditCard.holderName.placeholder': messages.cardHolderPlaceholder,
        'error.va.sf-cc-num.03': messages.unsupportedCard,
        'error.va.sf-cc-dat.01': messages.oldCard,
        'error.va.sf-cc-dat.02': messages.dateInFuture,
        'error.va.sf-cc-dat.03': messages.cardWillExpire,
        'error.va.gen.01': messages.incompleteData,
        'error.va.gen.02': messages.invalidData,
        ...(messages.enterCardNumber !== '' && { 'error.va.sf-cc-num.02': messages.enterCardNumber }),
        ...(messages.enterExpirationDate !== '' && { 'error.va.sf-cc-dat.04': messages.enterExpirationDate }),
        ...(messages.enterSecurityCode !== '' && { 'error.va.sf-cc-cvc.01': messages.enterSecurityCode }),
        ...(messages.enterFullExpirationDate !== '' && { 'error.va.sf-cc-dat.05': messages.enterFullExpirationDate }),
        ...(messages.allFieldsAreRequired !== '' && { 'form.instruction': messages.allFieldsAreRequired }),
        ...(messages.enterFulLSecurityCode !== '' && { 'error.va.sf-cc-cvc.02': messages.enterFulLSecurityCode }),
        ...(messages.enterFullCardNumber !== '' && { 'error.va.sf-cc-num.04': messages.enterFullCardNumber }),
      },
    },
  });
};

/**
 * This method mounts adyen action element into DOM
 *
 * @param container - container where to mount the action element
 * @param adyenLib - adyen library from where we mount the element
 * @param adyenService - adyen service which determines what element to mount (CARD, BLIK, IDEAL)
 * @param selectedStoredMethod - selected stored method if there is any
 * @param adyenServiceSettings - service configuration for certain type of service
 *
 * @returns {TAdyenActionElement} action element which should be used for cleanups (unmounts)
 */
export const mountAdyenElement = (
  container: HTMLDivElement,
  adyenLib: ICore,
  adyenService: TAdyenService,
  selectedStoredMethod: string | null,
  onSubmitAction: (data: IOnSubmitActionProps) => Promise<CheckoutAdvancedFlowResponse | void>,
  onSubmitInternal: (submitResponse: IAdyenWebPaymentResponse | null) => void,
  logger: LoggerType,
  validateForm?: () => void,
  adyenServiceSettings?: TAdyenServiceSettings,
  messages?: IAdyenPlaceholderMessages,
): TAdyenActionElement => {
  const actionElement = createActionElement(
    adyenLib,
    adyenService,
    selectedStoredMethod,
    onSubmitAction,
    onSubmitInternal,
    logger,
    validateForm,
    adyenServiceSettings,
    messages,
  );
  actionElement.mount(container);

  const blikInput = document.querySelector('.adyen-checkout__field--blikCode input');
  if (blikInput instanceof HTMLInputElement) {
    blikInput.placeholder = messages?.blikCodePlaceHolder ?? '';
  }

  const mbWayInput = document.querySelector('.adyen-checkout__mb-way input');
  if (mbWayInput instanceof HTMLInputElement) {
    mbWayInput.placeholder = messages?.mbWayCodePlaceHolder ?? '';
  }

  return actionElement;
};

/**
 * This method handles additional action if action is required by adyen
 * @param adyenService - concrete type of  service
 * @param element - action element on which action should be performed
 * @param adyenAction - action that should be performed
 */
export const handleAdditionalAction = (
  adyenService: TAdyenService,
  element: TAdyenActionElement | null,
  adyenAction?: TAdyenAction,
) => {
  if (!adyenAction) {
    return;
  }

  /**
   * There is a deviation from standard process for CARD service redirect methods
   * These kind of actions are required for 3DS1 process, and we have to make manual submit of a from and redirect to adyen
   */
  if (
    (adyenService === CARD_SERVICE || adyenService === BANCONTACT_SERVICE || adyenService === GOOGLE_PAY_SERVICE) &&
    adyenAction.type === 'redirect'
  ) {
    handleThreeDsOneCardAction(adyenAction);
    return;
  }

  handleStandardAction(adyenAction, element);
};

/**
 * This method handles standard adyen additional action
 * @param adyenAction - action to be performed
 * @param element - action element on which action should be performed
 */
const handleStandardAction = (adyenAction: TAdyenAction, element: TAdyenActionElement | null) => {
  if (!element) {
    return;
  }

  const action = mapAdyenActionFromDomain(adyenAction);

  if (action) {
    element.handleAction(action);
  }
};

/**
 * This method is used for adyen card 3DS1 process.
 * It creates form and submits it to adyen
 * @param adyenAction - action of redirect to be performed
 */
const handleThreeDsOneCardAction = (adyenAction: ICheckoutRedirectAction) => {
  if (!adyenAction.data) {
    return;
  }

  const paReq = adyenAction.data?.reduce((prev, x) => (x.key === THREE_DS_ONE_PARAM_PA_REQ ? x.value : prev), '');
  const termUrl = adyenAction.data?.reduce((prev, x) => (x.key === THREE_DS_ONE_PARAM_TERM_URL ? x.value : prev), '');
  const md = adyenAction.data?.reduce((prev, x) => (x.key === THREE_DS_ONE_PARAM_MD ? x.value : prev), '');

  createAndSubmitThreeDsAuthForm(adyenAction.url ?? '', paReq, md, termUrl);
};

/**
 * This method creates action element for adyen payment.
 * It either creates clear action element or element with one of the stored payment methods.
 *
 * @param adyenLib - adyen library
 * @param adyenService - certain type of service
 * @param selectedStoredMethod - selected stored payment method if there is any
 * @param adyenServiceSettings - settings for concrete adyen service
 *
 * @returns {TAdyenActionElement} Action element
 */
const createActionElement = (
  adyenLib: ICore,
  adyenService: TAdyenService,
  selectedStoredMethod: string | null,
  onSubmitAction: (data: IOnSubmitActionProps) => Promise<CheckoutAdvancedFlowResponse | void>,
  onSubmitInternal: (submitResponse: IAdyenWebPaymentResponse | null) => void,
  logger: LoggerType,
  validateForm?: () => void,
  adyenServiceSettings?: TAdyenServiceSettings,
  messages?: IAdyenPlaceholderMessages,
): TAdyenActionElement => {
  const cleanedSettings = {
    ...adyenServiceSettings,
  };
  switch (adyenService) {
    case CARD_SERVICE:
      if (selectedStoredMethod) {
        return new Card(adyenLib, {
          ...adyenLib.paymentMethodsResponse.storedPaymentMethods.find(
            (x) => x.storedPaymentMethodId === selectedStoredMethod,
          ),
          placeholders: {
            cardNumber: messages?.cardNumberPlaceHolder,
            expiryDate: messages?.expiryDatePlaceHolder,
            holderName: messages?.cardHolderPlaceholder,
            securityCodeThreeDigits: messages?.cvvPlaceHolder,
          },
        });
      }
      return new Card(adyenLib, {
        ...adyenServiceSettings,
        placeholders: {
          cardNumber: messages?.cardNumberPlaceHolder,
          expiryDate: messages?.expiryDatePlaceHolder,
          holderName: messages?.cardHolderPlaceholder,
          securityCodeThreeDigits: messages?.cvvPlaceHolder,
        },
      });
    case BANCONTACT_SERVICE:
      if (selectedStoredMethod) {
        return new Bancontact(adyenLib, {
          ...adyenLib.paymentMethodsResponse.storedPaymentMethods.find(
            (x) => x.storedPaymentMethodId === selectedStoredMethod,
          ),
          placeholders: {
            cardNumber: messages?.cardNumberPlaceHolder,
            expiryDate: messages?.expiryDatePlaceHolder,
            holderName: messages?.cardHolderPlaceholder,
            securityCodeThreeDigits: messages?.cvvPlaceHolder,
          },
        });
      }
      return new Bancontact(adyenLib, {
        ...adyenServiceSettings,
        placeholders: {
          cardNumber: messages?.cardNumberPlaceHolder,
          expiryDate: messages?.expiryDatePlaceHolder,
          holderName: messages?.cardHolderPlaceholder,
          securityCodeThreeDigits: messages?.cvvPlaceHolder,
        },
      });
    case BLIK_SERVICE:
      return new Blik(adyenLib, cleanedSettings);
    case IDEAL_SERVICE:
      return new Redirect(adyenLib, { ...adyenServiceSettings, type: IDEAL_SERVICE });
    case SWISH_SERVICE:
      return new Swish(adyenLib, { ...adyenServiceSettings, type: SWISH_SERVICE });
    case MULTIBANCO_SERVICE:
      return new Multibanco(adyenLib, cleanedSettings);
    case APPLE_PAY_SERVICE:
      return new ApplePay(adyenLib, {
        ...cleanedSettings,
        onSubmit: (state, _, { resolve }) => {
          onSubmitAction({ paymentForm: state, onSubmit: onSubmitInternal, validateForm })
            .then((response) => {
              if (response) {
                resolve(response);
              }
            })
            .catch((error) => {
              logger({
                logLevel: ELogLevel.ERROR,
                logError: new Error(`Unable to submit Apple Pay payment due to ${error}`, { cause: error }),
              });
            });
        },
        onAuthorized: (_, { resolve }) => {
          resolve();
        },
      });
    case GOOGLE_PAY_SERVICE:
      return new GooglePay(adyenLib, {
        ...cleanedSettings,
        type: GOOGLE_PAY_SERVICE,
        buttonType: 'short',
        buttonColor: 'black',
        onSubmit: (state, _, { resolve }) => {
          onSubmitAction({ paymentForm: state, onSubmit: onSubmitInternal, validateForm })
            .then((response) => {
              if (response) {
                resolve(response);
              }
            })
            .catch((error) => {
              logger({
                logLevel: ELogLevel.ERROR,
                logError: new Error(`Unable to submit Google Pay payment due to ${error}`, { cause: error }),
              });
            });
        },
      });
    case MBWAY_SERVICE:
      return new MBWay(adyenLib, cleanedSettings);
    case TWINT_SERVICE:
      return new Twint(adyenLib, cleanedSettings);
    case BANCONTACT_MOBILE_SERVICE:
      return new BcmcMobile(adyenLib, cleanedSettings);
    case MOBILE_PAY_SERVICE:
      return new Redirect(adyenLib, { ...adyenServiceSettings, type: MOBILE_PAY_SERVICE });
    default:
      return new Card(adyenLib, { ...adyenServiceSettings });
  }
};
